/**
 * Flow-Builder (Schritt 3)
 *
 * The conversation in the right panel that replaces template picking: it shows what the
 * campaign already knows, asks a handful of questions as click cards, has the backend
 * generate steps + canvas, then asks about automatic replies and hands the updated campaign
 * back to the page so the canvas on the left updates.
 */

import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { Alert, Box, ButtonBase, Checkbox, Chip, CircularProgress, Stack, TextField, Typography } from '@mui/material';
import { applyFlowBuilder, getFlowBuilderContext } from 'API/linkedin-funnel';
import { ProductGroups } from 'enums';
import { Funnel } from 'features/co-pilot/co-pilot.types';
import { LinkedinAccount } from 'features/co-pilot/linkedin-accounts/linkedin-accounts.types';
import PaiButton from 'features/ui/base-components/pai-button';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Node } from 'reactflow';
import { makeStyles } from 'tss-react/mui';
import AiGeneratedMessaging from '../ai-generated-messaging';
import {
  blockingReason,
  decodeError,
  isAnswered,
  isRecruiting,
  nextQuestion,
  questionSequence,
  questionsBeforeGeneration,
  summaryChips,
  toAnswers,
  type DraftAnswers,
  type FlowBuilderContext,
  type FlowBuilderErrorCode,
  type FlowSummary,
  type QuestionId,
} from './flow-builder.logic';

interface Props {
  campaignId: string;
  productGroup: ProductGroups;
  /** True while the page is saving something else; the builder waits instead of racing it. */
  busy: boolean;
  nodes: Node[];
  linkedinAccount: LinkedinAccount | undefined;
  customPlaceholders: import('../../../campaign-builder.types').CustomPlaceholder[];
  fieldInUse: { [key: string]: boolean };
  handleSelectNodeFromList(node: Node): () => void;
  handleUnselectNodeFromList(): void;
  /** The page replaces its campaign state and the canvas with what the builder generated. */
  onApplied(campaign: Funnel): void;
  onOpenAutoReplySettings(): void;
  onOpenFlowCreationDialog(): void;
}

type Phase = 'loading' | 'context_error' | 'blocked' | 'asking' | 'generating' | 'done';

interface BuilderError {
  code: FlowBuilderErrorCode | 'unknown';
  message: string;
}

const FlowBuilder: React.FC<Props> = ({
  campaignId,
  productGroup,
  busy,
  nodes,
  linkedinAccount,
  customPlaceholders,
  fieldInUse,
  handleSelectNodeFromList,
  handleUnselectNodeFromList,
  onApplied,
  onOpenAutoReplySettings,
  onOpenFlowCreationDialog,
}) => {
  const { classes, cx } = useStyles();
  const { t } = useTranslation();

  const [context, setContext] = useState<FlowBuilderContext | null>(null);
  const [phase, setPhase] = useState<Phase>('loading');
  const [draft, setDraft] = useState<DraftAnswers>({});
  const [summary, setSummary] = useState<FlowSummary | null>(null);
  const [warnings, setWarnings] = useState<string[]>([]);
  const [error, setError] = useState<BuilderError | null>(null);
  const [bonusInput, setBonusInput] = useState('');
  const [autoReplyActiveSaved, setAutoReplyActiveSaved] = useState<boolean | null>(null);

  const loadContext = useCallback(async () => {
    setPhase('loading');
    setError(null);
    const res = await getFlowBuilderContext(campaignId);
    if (!res || res.error) {
      setPhase('context_error');
      return;
    }
    const ctx = res as FlowBuilderContext;
    setContext(ctx);
    setDraft({ autoReplyGoal: ctx.autoReplyProposal.goalofCommunication, autoReplyCases: ctx.autoReplyProposal.caseHandling.map((_, i) => i) });
    setAutoReplyActiveSaved(ctx.autoReplyProposal.isActive);
    if (ctx.previous) {
      setSummary(ctx.previous.summary);
      setPhase('done');
      return;
    }
    setSummary(null);
    setPhase(blockingReason(ctx) ? 'blocked' : 'asking');
  }, [campaignId]);

  useEffect(() => {
    void loadContext();
  }, [loadContext]);

  const sequence = useMemo(() => (context ? questionSequence(context) : []), [context]);
  const beforeGeneration = useMemo(() => (context ? questionsBeforeGeneration(context) : []), [context]);
  const current = context ? nextQuestion(sequence, draft) : null;
  const readyToGenerate = context !== null && nextQuestion(beforeGeneration, draft) === null;

  const generate = useCallback(
    async (includeAutoReply: boolean) => {
      if (!context) return;
      setPhase('generating');
      setError(null);
      const res = await applyFlowBuilder(campaignId, toAnswers(context, draft, includeAutoReply) as unknown as Record<string, unknown>);
      if (!res || res.error) {
        setError(decodeError(res));
        setPhase('asking');
        return;
      }
      onApplied(res.campaign as Funnel);
      setSummary(res.summary as FlowSummary);
      setWarnings(Array.isArray(res.warnings) ? res.warnings : []);
      if (includeAutoReply) {
        setAutoReplyActiveSaved(draft.autoReplyActive === true);
        setPhase('done');
      } else {
        setPhase('asking');
      }
    },
    [campaignId, context, draft, onApplied]
  );

  // The flow is generated as soon as everything before the auto-reply question is answered,
  // so the canvas fills while the last question is still open.
  useEffect(() => {
    if (phase === 'asking' && readyToGenerate && summary === null && !error && !busy) {
      void generate(false);
    }
  }, [phase, readyToGenerate, summary, error, busy, generate]);

  // Once the auto-reply answer is in, the second apply carries it. Triggered here rather than
  // in `answer` so the draft state is current.
  const autoReplyAnswered = isAnswered('autoReply', draft);
  useEffect(() => {
    if (phase === 'asking' && autoReplyAnswered && summary !== null && !error && !busy) {
      void generate(true);
    }
  }, [phase, autoReplyAnswered, summary, error, busy, generate]);

  const answer = (patch: DraftAnswers) => {
    setError(null);
    setDraft((prev) => ({ ...prev, ...patch }));
  };

  const restart = () => {
    if (!context) return;
    setDraft({
      autoReplyGoal: context.autoReplyProposal.goalofCommunication,
      autoReplyCases: context.autoReplyProposal.caseHandling.map((_, i) => i),
    });
    setSummary(null);
    setWarnings([]);
    setError(null);
    setBonusInput('');
    setPhase(blockingReason(context) ? 'blocked' : 'asking');
  };

  if (phase === 'loading') {
    return (
      <Stack className={classes.root} alignItems={'center'} justifyContent={'center'} gap={2}>
        <CircularProgress size={28} />
        <Typography className={classes.muted}>{t('flowBuilderLoading')}</Typography>
      </Stack>
    );
  }

  if (phase === 'context_error' || !context) {
    return (
      <Stack className={classes.root} gap={2}>
        <Alert severity={'error'}>{t('flowBuilderContextError')}</Alert>
        <Box>
          <PaiButton text={t('flowBuilderRetry')} size={'small'} border={'rounded'} action={() => void loadContext()} />
        </Box>
      </Stack>
    );
  }

  const recruiting = isRecruiting(context);
  const languageName = (code: string) => (code === 'de' ? t('flowBuilderLangDe') : code === 'en' ? t('flowBuilderLangEn') : t('flowBuilderLangAutoShort'));

  const header = (
    <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'} className={classes.header}>
      <Stack direction={'row'} alignItems={'center'} gap={1}>
        <AccountTreeIcon className={classes.headerIcon} />
        <Typography className={classes.title}>{t('flowBuilderTitle')}</Typography>
      </Stack>
      {phase === 'done' ? (
        <Typography className={classes.doneBadge}>{t('flowBuilderDone')}</Typography>
      ) : current ? (
        <Typography className={classes.muted}>{t('flowBuilderQuestionOf', { n: sequence.indexOf(current) + 1, total: sequence.length })}</Typography>
      ) : null}
    </Stack>
  );

  if (phase === 'blocked') {
    const reason = blockingReason(context);
    return (
      <Stack className={classes.root} gap={2}>
        {header}
        <Alert severity={'info'}>
          {reason === 'not_draft' && t('flowBuilderBlockedNotDraft')}
          {reason === 'account_missing' && t('flowBuilderBlockedAccount')}
          {reason === 'briefing_incomplete' && t('flowBuilderBlockedBriefing', { fields: context.missingBriefingFields.map((f) => t(f)).join(', ') })}
        </Alert>
        <Typography className={classes.link} onClick={onOpenFlowCreationDialog}>
          {t('flowBuilderWithoutBuilder')}
        </Typography>
      </Stack>
    );
  }

  const knownChips: string[] = [];
  if (context.productType) knownChips.push(t(`flowBuilderKnownAccount_${context.productType}`));
  if (recruiting && context.recruitsFor) knownChips.push(context.recruitsFor === 'clients' ? t('flowBuilderKnownClients') : t('flowBuilderKnownOwn'));
  knownChips.push(t('flowBuilderKnownOffer'));
  knownChips.push(t('flowBuilderKnownLanguage', { lang: languageName(context.settings.language) }));

  const answeredLabel = (q: QuestionId): string => {
    switch (q) {
      case 'audience':
        return t(`flowBuilderAudience_${draft.audience}`);
      case 'recruitsFor':
        return draft.recruitsFor === 'clients' ? t('flowBuilderRecruitsClients') : t('flowBuilderRecruitsOwn');
      case 'inmailCredit':
        return draft.hasInMailCredit ? t('flowBuilderInmailYes') : t('flowBuilderInmailNo');
      case 'formality':
        return draft.formalSpeech ? t('flowBuilderFormal') : t('flowBuilderCasual');
      case 'language':
        if (draft.language === 'keep') return t('flowBuilderAnsweredKeep');
        if (draft.language === 'auto') return t('flowBuilderLangAuto');
        return t('flowBuilderLangPrimaryOnly', { primary: languageName(context.settings.language) });
      case 'referral':
        return draft.referralEnabled ? t('flowBuilderReferralAnswered', { bonus: draft.referralBonus }) : t('flowBuilderReferralNo');
      case 'autoReply':
        return draft.autoReplyActive ? t('flowBuilderAutoReplyYes') : t('flowBuilderAutoReplyNo');
      default:
        return '';
    }
  };

  const questionTitle = (q: QuestionId): string => {
    switch (q) {
      case 'audience':
        return t('flowBuilderQAudience');
      case 'recruitsFor':
        return t('flowBuilderQRecruitsFor');
      case 'inmailCredit':
        return t('flowBuilderQInmailCredit');
      case 'formality':
        return t('flowBuilderQFormality');
      case 'language':
        return t('flowBuilderQLanguage');
      case 'referral':
        return t('flowBuilderQReferral');
      case 'autoReply':
        return t('autoReplyQuestion');
      default:
        return '';
    }
  };

  const option = (label: string, desc: string | undefined, onClick: () => void, picked = false, disabled = false) => (
    <ButtonBase key={label} className={cx(classes.option, picked && classes.optionPicked)} onClick={onClick} disabled={disabled || busy} focusRipple>
      <Box className={classes.optionText}>
        <Typography className={classes.optionLabel}>{label}</Typography>
        {desc && <Typography className={classes.optionDesc}>{desc}</Typography>}
      </Box>
      {picked && <CheckCircleIcon color={'success'} fontSize={'small'} />}
    </ButtonBase>
  );

  const questionCard = (q: QuestionId) => {
    const primary = languageName(context.settings.language);
    const secondary = context.settings.secondaryLanguage && context.settings.secondaryLanguage !== 'none' ? languageName(context.settings.secondaryLanguage) : '';
    return (
      <Box className={classes.questionCard}>
        <Stack direction={'row'} alignItems={'center'} gap={1}>
          <Chip size={'small'} label={`${sequence.indexOf(q) + 1}/${sequence.length}`} />
          <Typography className={classes.question}>{questionTitle(q)}</Typography>
        </Stack>
        {q === 'audience' && (
          <Stack gap={0.75}>
            {option(t('flowBuilderAudience_new'), t('flowBuilderAudienceNewDesc'), () => answer({ audience: 'new' }))}
            {option(t('flowBuilderAudience_existing'), t('flowBuilderAudienceExistingDesc'), () => answer({ audience: 'existing' }))}
            {option(t('flowBuilderAudience_both'), t('flowBuilderAudienceBothDesc'), () => answer({ audience: 'both' }))}
          </Stack>
        )}
        {q === 'recruitsFor' && (
          <Stack gap={0.75}>
            {option(t('flowBuilderRecruitsOwn'), undefined, () => answer({ recruitsFor: 'own' }))}
            {option(t('flowBuilderRecruitsClients'), undefined, () => answer({ recruitsFor: 'clients' }))}
          </Stack>
        )}
        {q === 'inmailCredit' && (
          <Stack gap={0.75}>
            {option(t('flowBuilderInmailYes'), t('flowBuilderInmailYesDesc'), () => answer({ hasInMailCredit: true }))}
            {option(t('flowBuilderInmailNo'), t('flowBuilderInmailNoDesc'), () => answer({ hasInMailCredit: false }))}
          </Stack>
        )}
        {q === 'formality' && (
          <Stack direction={'row'} gap={0.75}>
            {option(t('flowBuilderCasual'), t('flowBuilderCasualDesc'), () => answer({ formalSpeech: false }))}
            {option(t('flowBuilderFormal'), t('flowBuilderFormalDesc'), () => answer({ formalSpeech: true }))}
          </Stack>
        )}
        {q === 'language' && (
          <Stack gap={0.75}>
            <Typography className={classes.muted}>{t('flowBuilderLanguageHint')}</Typography>
            {option(t('flowBuilderLangKeep', { primary, secondary: secondary ? t('flowBuilderLangWithSecondary', { secondary }) : '' }), undefined, () => answer({ language: 'keep' }))}
            {context.settings.language !== 'auto' && option(t('flowBuilderLangPrimaryOnly', { primary }), undefined, () => answer({ language: 'primaryOnly' }))}
            {option(t('flowBuilderLangAuto'), undefined, () => answer({ language: 'auto' }))}
          </Stack>
        )}
        {q === 'referral' && (
          <Stack gap={0.75}>
            {option(t('flowBuilderReferralYes'), t('flowBuilderReferralYesDesc'), () => answer({ referralEnabled: true }), draft.referralEnabled === true)}
            {draft.referralEnabled === true && (
              <Stack direction={'row'} gap={1} alignItems={'center'} className={classes.inlineInput}>
                <TextField
                  size={'small'}
                  fullWidth
                  label={t('flowBuilderReferralBonusLabel')}
                  value={bonusInput}
                  onChange={(e) => setBonusInput(e.target.value)}
                  inputProps={{ maxLength: 60 }}
                />
                <PaiButton text={t('flowBuilderApply')} size={'small'} border={'rounded'} disabled={!bonusInput.trim()} action={() => answer({ referralBonus: bonusInput.trim() })} />
              </Stack>
            )}
            {option(t('flowBuilderReferralNo'), t('flowBuilderReferralNoDesc'), () => answer({ referralEnabled: false, referralBonus: undefined }))}
          </Stack>
        )}
        {q === 'autoReply' && (
          <Stack gap={1}>
            <Stack direction={'row'} gap={0.75}>
              {option(
                t('flowBuilderAutoReplyYes'),
                undefined,
                () => setDraft((p) => ({ ...p, autoReplyActive: undefined, autoReplyDraftOpen: true })),
                draft.autoReplyActive === undefined && draft.autoReplyDraftOpen === true
              )}
              {option(t('flowBuilderAutoReplyNo'), undefined, () => answer({ autoReplyActive: false }))}
            </Stack>
            {draft.autoReplyDraftOpen && (
              <Stack gap={1} className={classes.proposal}>
                <Typography className={classes.sectionLabel}>{t('goalOfCommunication')}</Typography>
                <TextField
                  size={'small'}
                  fullWidth
                  multiline
                  value={draft.autoReplyGoal ?? ''}
                  onChange={(e) => setDraft((p) => ({ ...p, autoReplyGoal: e.target.value }))}
                  inputProps={{ maxLength: 2000 }}
                />
                <Typography className={classes.sectionLabel}>{t('cases')}</Typography>
                <Typography className={classes.muted}>{t('flowBuilderCasesHint')}</Typography>
                <Stack gap={0.5}>
                  {context.autoReplyProposal.caseHandling.map((c, i) => {
                    const checked = (draft.autoReplyCases ?? []).includes(i);
                    return (
                      <Stack key={i} direction={'row'} gap={1} alignItems={'flex-start'} className={cx(classes.caseRow, checked && classes.caseRowChecked)}>
                        <Checkbox
                          size={'small'}
                          checked={checked}
                          onChange={(e) =>
                            setDraft((p) => {
                              const selected = p.autoReplyCases ?? [];
                              const autoReplyCases = e.target.checked ? [...selected, i].sort((a, b) => a - b) : selected.filter((x) => x !== i);
                              return { ...p, autoReplyCases };
                            })
                          }
                        />
                        <Box>
                          <Typography className={classes.caseTitle}>{t('caseNumber', { number: i + 1 })} · {c.promptToIdentify}</Typography>
                          <Typography className={classes.optionDesc}>{c.promptToReply}</Typography>
                        </Box>
                      </Stack>
                    );
                  })}
                </Stack>
                <Stack direction={'row'} gap={1}>
                  <PaiButton
                    text={t('flowBuilderApply')}
                    size={'small'}
                    border={'rounded'}
                    disabled={!(draft.autoReplyGoal ?? '').trim() || (draft.autoReplyCases ?? []).length === 0}
                    action={() => answer({ autoReplyActive: true })}
                  />
                  <PaiButton text={t('flowBuilderAutoReplyOff')} size={'small'} border={'rounded'} action={() => answer({ autoReplyActive: false })} />
                </Stack>
              </Stack>
            )}
          </Stack>
        )}
        <Typography className={classes.muted}>{t('flowBuilderFreeTextHint')}</Typography>
      </Box>
    );
  };

  const errorText = (err: BuilderError) => {
    const key = `flowBuilderError_${err.code}`;
    const translated = t(key);
    return translated === key ? t('flowBuilderErrorGeneric', { message: err.message }) : translated;
  };

  return (
    <Stack className={classes.root} gap={1.5}>
      {header}

      <Stack gap={1.5} className={classes.transcript}>
        <Box className={classes.botBubble}>
          <Typography className={classes.bubbleText}>{t('flowBuilderGreeting')}</Typography>
          <Stack direction={'row'} flexWrap={'wrap'} gap={0.5} className={classes.chipRow}>
            {knownChips.map((c) => (
              <Chip key={c} size={'small'} label={c} variant={'outlined'} />
            ))}
          </Stack>
          <Typography className={classes.bubbleText}>{t('flowBuilderIntro', { count: sequence.length })}</Typography>
        </Box>

        {sequence
          .filter((q) => isAnswered(q, draft))
          .map((q) => (
            <React.Fragment key={q}>
              <Typography className={classes.answeredQuestion}>{questionTitle(q)}</Typography>
              <Box className={classes.userBubble}>
                <Typography className={classes.userBubbleText}>{answeredLabel(q)}</Typography>
              </Box>
            </React.Fragment>
          ))}

        {phase === 'generating' && (
          <Stack direction={'row'} gap={1} alignItems={'center'} className={classes.botBubble}>
            <CircularProgress size={16} />
            <Typography className={classes.bubbleText}>{t('flowBuilderGenerating')}</Typography>
          </Stack>
        )}

        {summary !== null && phase !== 'done' && (
          <Box className={classes.botBubble}>
            <Typography className={classes.bubbleText}>
              {t('flowBuilderGenerated', {
                steps: summary.stepCount,
                messages: summary.messageCount,
                inmails: summary.inmailCount > 0 ? t('flowBuilderGeneratedInmails', { count: summary.inmailCount }) : '',
              })}
            </Typography>
            <Typography className={classes.bubbleText}>{t('flowBuilderLastQuestion')}</Typography>
          </Box>
        )}

        {error && <Alert severity={error.code === 'unknown' ? 'error' : 'warning'}>{errorText(error)}</Alert>}

        {phase === 'asking' && current && (current !== 'autoReply' || summary !== null) && questionCard(current)}

        {phase === 'done' && summary && (
          <>
            <Box className={classes.botBubble}>
              <Typography className={classes.bubbleText}>{t('flowBuilderDoneText')}</Typography>
              <Stack direction={'row'} flexWrap={'wrap'} gap={0.5} className={classes.chipRow}>
                {summaryChips(context, summary, t).map((c) => (
                  <Chip key={c} size={'small'} label={c} color={'primary'} variant={'outlined'} />
                ))}
              </Stack>
              {warnings.map((w) => (
                <Typography key={w} className={classes.muted}>
                  {t(`flowBuilderWarning_${w}`)}
                </Typography>
              ))}
            </Box>
            <Box className={classes.list}>
              <AiGeneratedMessaging
                productGroup={productGroup}
                campaignId={campaignId}
                nodes={nodes}
                loading={busy}
                linkedinAccount={linkedinAccount}
                customPlaceholders={customPlaceholders}
                fieldInUse={fieldInUse}
                handleSelectNodeFromList={handleSelectNodeFromList}
                handleUnselectNodeFromList={handleUnselectNodeFromList}
              />
              <ButtonBase className={classes.row} onClick={onOpenAutoReplySettings} focusRipple>
                <AutoFixHighIcon className={classes.rowIcon} fontSize={'small'} />
                <Typography className={classes.rowText}>
                  {t('flowBuilderAutoReplyRow', {
                    state: autoReplyActiveSaved ? t('flowBuilderOn') : t('flowBuilderOff'),
                    count: autoReplyActiveSaved ? (draft.autoReplyCases ?? []).length : 0,
                  })}
                </Typography>
                <Typography className={classes.muted}>{t('flowBuilderEdit')}</Typography>
                <ChevronRightIcon fontSize={'small'} className={classes.rowIcon} />
              </ButtonBase>
            </Box>
            <Stack direction={'row'} gap={2} alignItems={'center'}>
              <PaiButton text={t('flowBuilderRegenerate')} size={'small'} border={'rounded'} action={restart} disabled={busy} />
              <Typography className={classes.link} onClick={onOpenFlowCreationDialog}>
                {t('flowBuilderWithoutBuilder')}
              </Typography>
            </Stack>
          </>
        )}

        {phase === 'asking' && (
          <Typography className={classes.link} onClick={onOpenFlowCreationDialog}>
            {t('flowBuilderWithoutBuilder')}
          </Typography>
        )}
      </Stack>
    </Stack>
  );
};

const useStyles = makeStyles()((theme) => ({
  root: {
    paddingBottom: '16px',
    marginBottom: '16px',
    borderBottom: `1px solid ${theme.palette.greys.light}`,
  },
  header: {
    paddingBottom: '8px',
  },
  headerIcon: {
    fontSize: theme.typography.pxToRem(24),
    color: theme.palette.primary.main,
  },
  title: {
    fontSize: theme.typography.pxToRem(15),
    color: theme.palette.primary.main,
    fontWeight: 'bold',
  },
  doneBadge: {
    fontSize: theme.typography.pxToRem(12),
    color: theme.palette.success.main,
    fontWeight: 500,
  },
  muted: {
    fontSize: theme.typography.pxToRem(12),
    color: theme.palette.text.secondary,
  },
  transcript: {
    display: 'flex',
    flexDirection: 'column',
  },
  botBubble: {
    padding: theme.spacing(1.25, 1.75),
    borderRadius: 12,
    borderTopLeftRadius: 4,
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.greys.lighter}`,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    maxWidth: 520,
  },
  bubbleText: {
    fontSize: theme.typography.pxToRem(13),
    lineHeight: 1.5,
  },
  chipRow: {
    marginTop: theme.spacing(0.5),
  },
  answeredQuestion: {
    fontSize: theme.typography.pxToRem(12),
    color: theme.palette.text.secondary,
    paddingLeft: theme.spacing(0.5),
  },
  userBubble: {
    alignSelf: 'flex-end',
    padding: theme.spacing(1, 1.5),
    borderRadius: 12,
    borderTopRightRadius: 4,
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    maxWidth: 420,
  },
  userBubbleText: {
    fontSize: theme.typography.pxToRem(13),
  },
  questionCard: {
    padding: theme.spacing(1.5),
    border: `1px solid ${theme.palette.primary.main}33`,
    borderRadius: theme.shape.borderRadius,
    backgroundColor: `${theme.palette.primary.main}06`,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    maxWidth: 520,
  },
  question: {
    fontSize: theme.typography.pxToRem(14),
    fontWeight: 600,
  },
  option: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(1, 1.5),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.background.paper,
    textAlign: 'left',
    justifyContent: 'flex-start',
    width: '100%',
    flex: 1,
    transition: theme.transitions.create(['border-color', 'background-color'], { duration: 120 }),
    '&:hover': {
      borderColor: theme.palette.primary.main,
      backgroundColor: `${theme.palette.primary.main}0d`,
    },
  },
  optionPicked: {
    borderColor: theme.palette.primary.main,
    backgroundColor: `${theme.palette.primary.main}14`,
  },
  optionText: {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    minWidth: 0,
  },
  optionLabel: {
    fontSize: theme.typography.pxToRem(13),
    fontWeight: 500,
  },
  optionDesc: {
    fontSize: theme.typography.pxToRem(11),
    color: theme.palette.text.secondary,
  },
  inlineInput: {
    paddingLeft: theme.spacing(1),
  },
  proposal: {
    padding: theme.spacing(1),
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.background.paper,
  },
  sectionLabel: {
    fontSize: theme.typography.pxToRem(11),
    color: theme.palette.text.secondary,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
  },
  caseRow: {
    padding: theme.spacing(0.5, 0.75),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
  },
  caseRowChecked: {
    borderColor: theme.palette.primary.main,
    backgroundColor: `${theme.palette.primary.main}0a`,
  },
  caseTitle: {
    fontSize: theme.typography.pxToRem(12),
    fontWeight: 600,
  },
  list: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.25),
    width: '100%',
    padding: theme.spacing(1, 1.5),
    borderRadius: 6,
    backgroundColor: theme.palette.background.paper,
    textAlign: 'left',
  },
  rowIcon: {
    color: theme.palette.primary.main,
  },
  rowText: {
    flexGrow: 1,
    fontSize: theme.typography.pxToRem(13),
  },
  link: {
    fontSize: theme.typography.pxToRem(12),
    color: theme.palette.primary.main,
    cursor: 'pointer',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
}));

export default FlowBuilder;
